/**
 * Prioritization Cycles List Page
 *
 * Lists cycles. Track readiness is conditions-based (not a wizard): participation,
 * item pool, criteria, value types, values, ranking. Conditions may be met in any order.
 *
 * Uses useEntityListState + EntityListToolbar for the same UX as the item detail view.
 */

import { isCycleDesignerComplete } from '@/features/cycle-designer/lib/cycleDesignerPhase';
import { useState, useMemo, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Pencil,
  Play,
  Calendar,
  CheckCircle2,
  Loader2,
  Archive,
  MoreVertical,
  Trash2,
  Layers,
  Repeat,
  Infinity as InfinityIcon,
  Zap,
  Circle,
  Star,
  ChevronRight,
} from 'lucide-react';
import { PageHeader as SharedPageHeader } from '@/components/prioritization/PageHeader';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useUIStore } from '@/stores/prioritization/uiStore';
import {
  evaluateTrackReadiness,
  type TrackReadinessStatus,
  type ReadinessCondition,
} from '@/lib/trackReadinessEvaluator';
import { getConditionLabel } from '@/lib/trackConditions';
import { toFriendlyDbError } from '@/lib/friendlyDbError';
import { cycleDeleteConsequence } from '@/lib/workspaceSessionRules';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { getReasonMessage } from '@/lib/authorization/reasonMessages';
import { Modal } from '@/components/ds';
import {
  type CycleMode,
  CYCLE_MODE_DESCRIPTIONS,
} from '@/types/prioritization/degapCycle';
import { usePinnedCycleStore } from '@/stores/prioritization/pinnedCycleStore';
import { useEntityListState } from '@/hooks/useEntityListState';
import { EntityListToolbar } from '@/components/shared/EntityListToolbar';
import { VirtualizedTable } from '@/components/collection/VirtualizedTable';
import type { VirtualizedTableColumn } from '@/components/collection/VirtualizedTable';
import { useCycleLifecycleDisplayMap } from '@/hooks/prioritization/useCycleLifecycleDisplayMap';
import { CycleLifecycleStatusBadge } from '@/components/prioritization/CycleLifecycleStatusBadge';
import { showsArchivedCycles, type CycleLifecycleDisplayStatus } from '@/lib/cycleLifecycleDisplayStatus';
import { ReverseActionButton } from '@/components/reversals/ReverseActionButton';
import { unarchiveCycle } from '@/lib/reversals/reversalsService';
import { CreateCycleDialog } from '@/components/prioritization/cycles/CreateCycleDialog';

// ── Readiness helpers (kept for CycleTrackReadiness component used in details) ─

const CONDITION_KEYS = ['s1_participation', 's2_pool', 's3_criteria', 's4_scales', 's5_values', 's6_tool'] as const;
const CONDITION_GRID_LABELS: Record<(typeof CONDITION_KEYS)[number], string> = {
  s1_participation: 'Part.', s2_pool: 'Pool', s3_criteria: 'Crit.',
  s4_scales: 'Scale', s5_values: 'Values', s6_tool: 'Rank',
};

function getConditionTooltip(key: string, condition: ReadinessCondition | null): string {
  const label = getConditionLabel(key);
  if (!condition) return `${label}: unknown`;
  if (condition.ready) return `${label}: met`;
  const reason = condition.blockers[0] ?? condition.warnings[0] ?? condition.actions[0] ?? 'not yet';
  return `${label}: ${reason}`;
}

function applyValuesConvergedOverride(status: TrackReadinessStatus | null, valuesConverged: boolean): TrackReadinessStatus | null {
  if (!status || !valuesConverged || status.s5_values.ready) return status;
  const s1_4 = status.s1_participation.ready && status.s2_pool.ready && status.s3_criteria.ready && status.s4_scales.ready;
  const s5Ready: ReadinessCondition = { ready: true, level: 'ready', blockers: [], warnings: [], actions: ['View'] };
  const s6Ready: ReadinessCondition = s1_4
    ? { ready: true, level: 'ready', blockers: [], warnings: [], actions: ['Run ranking session'] }
    : status.s6_tool;
  return {
    ...status, s5_values: s5Ready, s6_tool: s6Ready,
    allPrerequisitesMet: s1_4 && s6Ready.ready, s6Ready: s1_4 && s6Ready.ready,
    summary: {
      readyCount: [status.s1_participation, status.s2_pool, status.s3_criteria, status.s4_scales, s5Ready, s6Ready].filter(c => c.level === 'ready').length,
      warningCount: [status.s1_participation, status.s2_pool, status.s3_criteria, status.s4_scales, s5Ready, s6Ready].filter(c => c.level === 'warning').length,
      blockedCount: [status.s1_participation, status.s2_pool, status.s3_criteria, status.s4_scales, s5Ready, s6Ready].filter(c => c.level === 'blocked').length,
    },
  };
}

export function CycleTrackReadiness({
  trackIds,
  trackNames,
  valuesConvergedByTrackId = {},
}: {
  trackIds: string[];
  trackNames: string[];
  valuesConvergedByTrackId?: Record<string, boolean>;
}) {
  const [statuses, setStatuses] = useState<(TrackReadinessStatus | null)[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (trackIds.length === 0) { setStatuses([]); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    Promise.all(trackIds.map(id => evaluateTrackReadiness(id)))
      .then(results => { if (!cancelled) setStatuses(results.map(r => r ?? null)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // Pre-existing dependency-array warning; silenced only because editing this
  // file pulls it into CI's changed-file lint at --max-warnings=0.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackIds.join(',')]);

  if (trackIds.length === 0) return null;
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-[var(--text-ter)] mb-3">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading track readiness…
      </div>
    );
  }
  return (
    <div className="mb-3">
      <p className="text-xs font-medium text-[var(--text-ter)] mb-1.5">Track conditions</p>
      <div className="flex items-center gap-2 mb-1.5 text-xs text-[var(--text-ter)]">
        <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-[var(--green)]" /> met</span>
        <span>·</span>
        <span className="text-[var(--text-ter)]">not yet</span>
      </div>
      <div className="flex items-center gap-2 text-xs mb-0.5 pl-24">
        {CONDITION_KEYS.map(key => (
          <div key={key} className="w-8 flex flex-col items-center min-w-0" title={getConditionLabel(key)}>
            <span className="text-[10px] font-medium text-[var(--text-ter)] truncate max-w-full">{CONDITION_GRID_LABELS[key]}</span>
          </div>
        ))}
      </div>
      <div className="space-y-1">
        {trackNames.map((name, i) => {
          const status = applyValuesConvergedOverride(statuses[i], !!valuesConvergedByTrackId[trackIds[i]]);
          return (
            <div key={trackIds[i]} className="flex items-center gap-2 text-xs">
              <span className="w-24 truncate text-[var(--text-sec)]" title={name}>{name}</span>
              <div className="flex items-center gap-0.5">
                {CONDITION_KEYS.map(key => {
                  const condition = status ? status[key] : null;
                  const ready = condition?.ready ?? false;
                  return (
                    <div key={key} className={`w-8 h-6 rounded flex items-center justify-center cursor-help flex-shrink-0 ${ready ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'}`} title={getConditionTooltip(key, condition)}>
                      {ready ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" strokeWidth={2} />}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Cycle type ────────────────────────────────────────────────────────────────

type CycleItem = {
  id: string;
  name: string;
  description?: string;
  status: string;
  cadence?: { mode: string; frequency?: string } | null;
  createdAt: Date | string;
  updatedAt: Date | string;
};

function formatDate(v: Date | string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

function lifecycleStatusCell(
  cycleId: string,
  displayByCycleId: Record<string, CycleLifecycleDisplayStatus>,
  loading: boolean,
) {
  if (loading && displayByCycleId[cycleId] === undefined) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-ter)] min-w-[3.25rem] justify-center animate-pulse">
        …
      </span>
    );
  }
  return <CycleLifecycleStatusBadge status={displayByCycleId[cycleId] ?? 'draft'} />;
}

function modeInfo(cadence?: { mode: string; frequency?: string } | null) {
  if (!cadence) return { label: '—', Icon: Zap };
  const label = cadence.mode === 'periodic' && cadence.frequency
    ? cadence.frequency
    : CYCLE_MODE_DESCRIPTIONS[cadence.mode as CycleMode]?.label ?? cadence.mode;
  const Icon = cadence.mode === 'periodic' ? Repeat : cadence.mode === 'continuous' ? InfinityIcon : Zap;
  return { label, Icon };
}

// ── Column definitions ────────────────────────────────────────────────────────

const ALL_COLUMNS = [
  { key: 'name',      label: 'Cycle',    defaultVisible: true  },
  { key: 'status',    label: 'Status',   defaultVisible: true  },
  { key: 'mode',      label: 'Mode',     defaultVisible: true  },
  { key: 'tracks',    label: 'Tracks',   defaultVisible: true  },
  { key: 'createdAt', label: 'Created',  defaultVisible: true  },
  { key: 'updatedAt', label: 'Modified', defaultVisible: false },
];

const SORT_OPTIONS = [
  { field: 'name',      label: 'Name (A–Z)' },
  { field: 'status',    label: 'Status' },
  { field: 'createdAt', label: 'Date Created' },
  { field: 'updatedAt', label: 'Last Modified' },
];

const GROUP_OPTIONS = [
  { field: 'status',       label: 'Status' },
  { field: 'cadence.mode', label: 'Cycle Type' },
];

const FILTER_FIELDS = [
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'active',    label: 'Active' },
      { value: 'draft',     label: 'Draft' },
      { value: 'paused',    label: 'Paused' },
      { value: 'ratified',  label: 'Ratified' },
      { value: 'published', label: 'Published' },
      { value: 'closed',    label: 'Closed' },
    ],
  },
  {
    key: 'cadence.mode',
    label: 'Cycle Type',
    options: [
      { value: 'episodic',   label: 'Episodic' },
      { value: 'periodic',   label: 'Periodic' },
      { value: 'continuous', label: 'Continuous' },
    ],
  },
];

const DEFAULT_SORT_FIELD = 'updatedAt';
const DEFAULT_SORT_DIR = 'desc' as const;

// ── Row action menu ───────────────────────────────────────────────────────────

function CycleRowActions({ cycle, onArchive, onDelete, onUnarchived, canDelete, canArchive, launched, canDesign }: {
  cycle: CycleItem;
  onArchive: () => void;
  onDelete: () => void;
  /** Refetch after a successful restore. */
  onUnarchived: () => void;
  canDelete: boolean;
  /** Workspace admin — archiving hides the cycle for everyone. Restore uses the
      same gate. */
  canArchive: boolean;
  /** Cycle has been through the designer — metadata.designerLaunchedAt is set. */
  launched: boolean;
  /** Viewer would actually reach the designer (cycle owner or workspace admin). */
  canDesign: boolean;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const anchor = anchorRef.current.getBoundingClientRect();
    const menuWidth = 144; // w-36
    const menuHeight = 88; // approximate: two rows (+ padding), avoids clipping below viewport
    let top = anchor.bottom + 4;
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, anchor.top - menuHeight - 4);
    }
    let left = anchor.right - menuWidth;
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    setMenuPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  const menuPortal =
    open &&
    typeof document !== 'undefined' &&
    createPortal(
      <>
        <div className="fixed inset-0 z-[80]" aria-hidden onClick={() => setOpen(false)} />
        <div
          role="menu"
          className="fixed z-[90] w-36 bg-[var(--bg-raised)] shadow-lg border border-[var(--rule)] py-1 dark:bg-[var(--bg-raised)] dark:border-[var(--rule)]"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          {/* Archiving is a workspace-admin action (owner ruling): it removes a
              cycle from everyone's view, not just your own. `admin.settings` is
              the permission the workspace-admin surfaces already use. */}
          {cycle.status !== 'archived' && canArchive && (
            <button
              type="button"
              onClick={() => {
                onArchive();
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[var(--text-sec)] hover:bg-[var(--surface)] dark:text-[var(--text-pri)] dark:hover:bg-[var(--hover-tint)]"
            >
              <Archive className="h-3.5 w-3.5" />
              Archive
            </button>
          )}
          {cycle.status === 'archived' && canArchive && (
            <div onClick={() => setOpen(false)}>
              <ReverseActionButton
                label="Restore cycle"
                confirmTitle="Restore cycle"
                explain={`This will take "${cycle.name}" off the shelf and back into the active cycles list.`}
                run={(reason) => unarchiveCycle(cycle.id, reason)}
                onDone={onUnarchived}
                variant="ghost"
                size="sm"
                className="!w-full !justify-start !gap-2 !px-3 !py-2 !text-xs !font-normal text-[var(--text-sec)] dark:text-[var(--text-pri)]"
              />
            </div>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => {
                onDelete();
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/40"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          )}
        </div>
      </>,
      document.body,
    );

  return (
    <div className="flex items-center gap-1 justify-end flex-shrink-0 min-w-[7.5rem]" onClick={(e) => e.stopPropagation()}>
      {/* The row's verb matches where the click lands. An unlaunched cycle opens
          the Cycle Designer, a launched one opens cycle detail — same URL, the
          redirect decides. Two identical-looking rows behaving differently is
          how people end up somewhere they did not ask for.

          The verb is gated on the same owner/admin test the redirect uses
          (`shouldUseCycleDesigner`): someone without design rights lands on
          detail regardless, so promising them "Continue design" would be a lie.
          They still see the DESIGN badge — the cycle's state is true for
          everyone, only the action differs. */}
      <Link
        to={`/prioritization/cycles/${cycle.id}`}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 hover:bg-primary-100 transition-colors"
      >
        {!launched && canDesign ? (
          <>
            <Pencil className="h-3 w-3" />
            Continue design
          </>
        ) : (
          <>
            <Play className="h-3 w-3" />
            Open
          </>
        )}
      </Link>
      <button
        ref={anchorRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="p-1.5 hover:bg-[var(--hover-tint)] transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Cycle actions"
      >
        <MoreVertical className="h-3.5 w-3.5 text-[var(--text-ter)]" />
      </button>
      {menuPortal}
    </div>
  );
}

// ── Filtering + sorting helpers ───────────────────────────────────────────────

function matchesSearch(cycle: CycleItem, q: string): boolean {
  if (!q) return true;
  const lower = q.toLowerCase();
  return cycle.name.toLowerCase().includes(lower) ||
    (cycle.description ?? '').toLowerCase().includes(lower);
}

function sortCycles(
  cycles: CycleItem[],
  field: string,
  dir: 'asc' | 'desc',
  displayByCycleId: Record<string, CycleLifecycleDisplayStatus>,
): CycleItem[] {
  return [...cycles].sort((a, b) => {
    let valA: string | number = '';
    let valB: string | number = '';
    if (field === 'name') { valA = a.name; valB = b.name; }
    else if (field === 'status') {
      valA = displayByCycleId[a.id] ?? a.status;
      valB = displayByCycleId[b.id] ?? b.status;
    }
    else if (field === 'createdAt') { valA = new Date(a.createdAt).getTime(); valB = new Date(b.createdAt).getTime(); }
    else if (field === 'updatedAt') { valA = new Date(a.updatedAt).getTime(); valB = new Date(b.updatedAt).getTime(); }
    if (valA < valB) return dir === 'asc' ? -1 : 1;
    if (valA > valB) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ProcessList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const addNotification = useUIStore(state => state.addNotification);

  const can = useUserStore((s) => s.can);
  // can() reads the store, not React state: follow permissionsLoaded so this redraws when rights arrive.
  useUserStore((s) => s.permissionsLoaded);
  const canCreateCycle = can('process.create');
  // Delete is permanent, archive is reversible, so delete carries the higher
  // gate. These were the wrong way round: a user who could not archive a cycle
  // could still destroy one.
  const canDeleteCycle = can('admin.settings');
  // Same test `shouldUseCycleDesigner` applies before redirecting to the designer:
  // without it the click lands on cycle detail, whatever the row says.
  const canDesignCycles = can('process.edit');
  const canArchiveCycle = can('process.edit');

  const cycles = useDegapProcessStore(state => state.cycles);
  const cycleIdListForLifecycle = useMemo(() => cycles.map((c) => c.id), [cycles]);
  const { displayByCycleId, loading: lifecycleDisplayLoading } = useCycleLifecycleDisplayMap(cycleIdListForLifecycle);
  const fetchFromSupabase = useDegapProcessStore(state => state.fetchFromSupabase);
  const tracks = useTrackStore(state => state.tracks);
  const fetchAllTracks = useTrackStore(state => state.fetchAllTracks);
  const syncState = useDegapProcessStore(state => state.syncState);
  const setActiveCycle = useDegapProcessStore(state => state.setActiveCycle);
  const deleteCycle = useDegapProcessStore(state => state.deleteCycle);
  const archiveCycle = useDegapProcessStore(state => state.archiveCycle);
  const updateCycle = useDegapProcessStore(state => state.updateCycle);

  // ── Inline name edit state ───────────────────────────────────────────────────
  const [editingCycleId, setEditingCycleId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  const startEditingCycle = useCallback((cycle: CycleItem, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingCycleId(cycle.id);
    setEditingName(cycle.name);
  }, []);

  const commitCycleRename = useCallback(async () => {
    if (!editingCycleId || !editingName.trim()) { setEditingCycleId(null); return; }
    const result = await updateCycle(editingCycleId, { name: editingName.trim() });
    if (!result.success) {
      addNotification({ type: 'error', title: 'Rename failed', message: result.error ?? 'Could not update cycle name.', duration: 5000 });
    }
    setEditingCycleId(null);
  }, [editingCycleId, editingName, updateCycle, addNotification]);

  useEffect(() => {
    if (editingCycleId && editInputRef.current) editInputRef.current.select();
  }, [editingCycleId]);
  const pinnedCycleId = usePinnedCycleStore(state => state.pinnedCycleId);
  const enterWorkspace = usePinnedCycleStore(state => state.enterWorkspace);
  const exitWorkspace = usePinnedCycleStore(state => state.exitWorkspace);



  useEffect(() => {
    fetchFromSupabase();
    fetchAllTracks();
  }, [fetchFromSupabase, fetchAllTracks]);

  // ── List state ──────────────────────────────────────────────────────────────
  const listState = useEntityListState({
    allColumns: ALL_COLUMNS,
    defaultSortField: DEFAULT_SORT_FIELD,
    defaultSortDirection: DEFAULT_SORT_DIR,
    storageKey: 'cycles',
    filterFields: FILTER_FIELDS,
  });

  // ── Track count per cycle ───────────────────────────────────────────────────
  const trackCountByCycle = useMemo(() => {
    const map: Record<string, number> = {};
    for (const t of tracks) map[t.cycleId] = (map[t.cycleId] ?? 0) + 1;
    return map;
  }, [tracks]);

  // ── Filtered + sorted items ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = cycles as CycleItem[];
    // Archiving takes a cycle out of view — that is the entire point of it.
    // Before this, archiving only changed `status`, so the row stayed exactly
    // where it was and the action appeared to do nothing. Archived cycles remain
    // reachable by filtering Status = Closed; they are hidden, not deleted.
    if (!showsArchivedCycles(listState.activeFilters.status)) {
      result = result.filter(c => c.status !== 'archived');
    }
    if (listState.searchQuery) result = result.filter(c => matchesSearch(c, listState.searchQuery));
    result = listState.applyFilters(result, (cycle, field) => {
      if (field === 'status') return displayByCycleId[cycle.id] ?? (cycle as CycleItem).status;
      if (field === 'cadence.mode') return (cycle as CycleItem).cadence?.mode;
      return undefined;
    });
    result = sortCycles(result, listState.sort.field, listState.sort.direction, displayByCycleId);
    return result;
  // Pre-existing dependency-array warning; silenced only because editing this
  // file pulls it into CI's changed-file lint at --max-warnings=0.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycles, listState.searchQuery, listState.sort, listState.applyFilters, displayByCycleId]);

  // ── Grouping ────────────────────────────────────────────────────────────────
  const groupedItems = useMemo(() => {
    if (!listState.groupBy) return null;
    const groups: Map<string, CycleItem[]> = new Map();
    for (const cycle of filtered) {
      let key = '';
      if (listState.groupBy === 'status') key = displayByCycleId[cycle.id] ?? cycle.status;
      else if (listState.groupBy === 'cadence.mode') key = cycle.cadence?.mode ?? 'unknown';
      const arr = groups.get(key) ?? [];
      arr.push(cycle);
      groups.set(key, arr);
    }
    return groups;
  }, [filtered, listState.groupBy, displayByCycleId]);

  // Row height by density
  const rowHeight = listState.density === 'compact' ? 40 : listState.density === 'comfortable' ? 72 : 56;

  // ── Create form ─────────────────────────────────────────────────────────────
  // One create-cycle form everywhere (owner ruling #7): CreateCycleDialog.
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [pendingConfiguration, setPendingConfiguration] = useState<{ id: string; name: string | null } | null>(null);
  // Catchball prompt: set after successful creation when config requires Catchball
  const [catchballPromptCycleId, setCatchballPromptCycleId] = useState<string | null>(null);

  // Auto-open the create form when arriving from ConfigurationSelector
  // (?create=1&config_id=<uuid>&config_name=<name>) or at /prioritization/cycles/new.
  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    const configId = searchParams.get('config_id');
    setPendingConfiguration(configId ? { id: configId, name: searchParams.get('config_name') } : null);
    setShowCreateForm(true);
    // Remove the params so refresh doesn't re-open the form
    setSearchParams({}, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  useEffect(() => {
    if (location.pathname.endsWith('/cycles/new') && canCreateCycle) setShowCreateForm(true);
  }, [location.pathname, canCreateCycle]);
  const closeCreateForm = () => {
    setShowCreateForm(false);
    setPendingConfiguration(null);
    if (location.pathname.endsWith('/cycles/new')) navigate('/prioritization/cycles', { replace: true });
  };

  // A refused delete used to do nothing at all: the store returned the reason
  // (and fired a `cycle-deletion-blocked` event nobody listened to) and this
  // page dropped it. Now the reason is shown, with Archive as the way forward.
  const [deleteBlocked, setDeleteBlocked] = useState<{ id: string; name: string; reason: string; canArchive: boolean } | null>(null);
  const [archivingBlocked, setArchivingBlocked] = useState(false);

  const handleDelete = async (id: string, name: string) => {
    // Said before the confirmation: what the workspace's rule does to the open sessions.
    const consequence = await cycleDeleteConsequence(id, useTenantStore.getState().getCurrentWorkspace?.()?.cycleDeletionPolicy);
    if (consequence.blocked) {
      const status = useDegapProcessStore.getState().cycles.find((c) => c.id === id)?.status;
      setDeleteBlocked({ id, name, reason: consequence.line, canArchive: canArchiveCycle && status !== 'archived' });
      return;
    }
    if (!confirm(`Are you sure you want to delete "${name}"? ${consequence.line ? `${consequence.line} ` : ''}This cannot be undone.`)) return;
    const result = await deleteCycle(id);
    if (result.success) {
      addNotification({ type: 'success', title: 'Cycle deleted', message: `"${name}" was deleted.`, duration: 3000 });
      return;
    }
    const raw = result.error ?? 'The cycle could not be deleted.';
    // Policy denials come back as a bare reason code (e.g. NOT_OWNER).
    const reason = /^[A-Z][A-Z0-9_]+$/.test(raw) ? getReasonMessage(raw) : toFriendlyDbError(raw, 'ProcessList.handleDelete');
    const status = useDegapProcessStore.getState().cycles.find((c) => c.id === id)?.status;
    setDeleteBlocked({ id, name, reason, canArchive: canArchiveCycle && status !== 'archived' });
  };

  const handleArchiveInstead = async () => {
    if (!deleteBlocked) return;
    setArchivingBlocked(true);
    await archiveCycle(deleteBlocked.id);
    setArchivingBlocked(false);
    const nowArchived =
      useDegapProcessStore.getState().cycles.find((c) => c.id === deleteBlocked.id)?.status === 'archived';
    if (nowArchived) {
      addNotification({
        type: 'success',
        title: 'Cycle archived',
        message: `"${deleteBlocked.name}" is archived. It is hidden from the active list and can be restored.`,
        duration: 5000,
      });
      setDeleteBlocked(null);
    } else {
      addNotification({
        type: 'error',
        title: 'Cycle was not archived',
        message: 'You may not have permission to archive this cycle.',
        duration: 6000,
      });
    }
  };

  // ── Table columns ───────────────────────────────────────────────────────────
  const tableColumns = useMemo((): VirtualizedTableColumn<CycleItem>[] => {
    const colMap: Record<string, VirtualizedTableColumn<CycleItem>> = {
      name: {
        key: 'name', label: 'Cycle', width: '35%', sortable: true,
        render: (cycle) => {
          const isPinned = pinnedCycleId === cycle.id;
          const isEditing = editingCycleId === cycle.id;
          return (
            <div className="flex items-center gap-2 min-w-0" onClick={e => e.stopPropagation()}>
              <button onClick={e => { e.stopPropagation(); if (isPinned) { exitWorkspace(); } else { enterWorkspace(cycle.id, cycle.name); navigate(`/prioritization/cycles/${cycle.id}`); } }}
                className={`flex-shrink-0 p-0.5 rounded transition-colors ${isPinned ? 'text-[var(--mustard)]' : 'text-[var(--text-ter)] hover:text-[var(--mustard)]'}`}>
                <Star className={`h-3.5 w-3.5 ${isPinned ? 'fill-amber-400' : ''}`} />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {isEditing ? (
                    <input
                      ref={editInputRef}
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onBlur={commitCycleRename}
                      onKeyDown={e => { if (e.key === 'Enter') commitCycleRename(); if (e.key === 'Escape') setEditingCycleId(null); }}
                      className="text-sm font-medium text-[var(--text-pri)] border border-primary-400 rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary-400 bg-[var(--bg-raised)] min-w-0 w-full max-w-[240px]"
                      onClick={e => e.stopPropagation()}
                    />
                  ) : (
                    <Link
                      to={`/prioritization/cycles/${cycle.id}`}
                      onClick={e => e.stopPropagation()}
                      onDoubleClick={e => startEditingCycle(cycle, e)}
                      className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-600 truncate"
                      title="Double-click to rename"
                    >
                      {cycle.name}
                    </Link>
                  )}
                  {isPinned && <span className="text-[10px] font-bold bg-[var(--mustard-dim)] text-[var(--mustard)] px-1.5 py-0.5 rounded uppercase tracking-wide">Active</span>}
                </div>
                {cycle.description && listState.density !== 'compact' && <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">{cycle.description}</p>}
              </div>
            </div>
          );
        },
      },
      status: {
        key: 'status', label: 'Status', width: '12%', sortable: true,
        render: (cycle) => lifecycleStatusCell(cycle.id, displayByCycleId, lifecycleDisplayLoading),
      },
      mode: {
        key: 'mode', label: 'Mode', width: '13%',
        render: (cycle) => {
          const { label, Icon } = modeInfo(cycle.cadence);
          return <span className="inline-flex items-center gap-1.5 text-sm text-[var(--text-sec)]"><Icon className="h-3.5 w-3.5 text-[var(--text-ter)]" />{label}</span>;
        },
      },
      tracks: {
        key: 'tracks', label: 'Tracks', width: '8%',
        render: (cycle) => (
          <span className="inline-flex items-center gap-1.5 text-sm text-[var(--text-sec)]"><Layers className="h-3.5 w-3.5 text-[var(--text-ter)]" />{trackCountByCycle[cycle.id] ?? 0}</span>
        ),
      },
      createdAt: {
        key: 'createdAt', label: 'Created', width: '10%', sortable: true,
        render: (cycle) => <span className="text-sm text-[var(--text-ter)]">{formatDate(cycle.createdAt)}</span>,
      },
      updatedAt: {
        key: 'updatedAt', label: 'Modified', width: '10%', sortable: true,
        render: (cycle) => <span className="text-sm text-[var(--text-ter)]">{formatDate(cycle.updatedAt)}</span>,
      },
    };
    const cols = listState.visibleColumns.map(key => colMap[key]).filter(Boolean);
    cols.push({
      key: 'actions', label: '', width: '12%', align: 'right',
      render: (cycle) => (
        <CycleRowActions cycle={cycle} onArchive={() => archiveCycle(cycle.id)} onDelete={() => handleDelete(cycle.id, cycle.name)} onUnarchived={() => fetchFromSupabase()} canDelete={canDeleteCycle} canArchive={canArchiveCycle} launched={isCycleDesignerComplete(cycle as never)} canDesign={canDesignCycles} />
      ),
    });
    return cols;
  // Pre-existing dependency-array warning; silenced only because editing this
  // file pulls it into CI's changed-file lint at --max-warnings=0.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listState.visibleColumns, listState.density, pinnedCycleId, trackCountByCycle, canDeleteCycle, canDesignCycles, canArchiveCycle, enterWorkspace, exitWorkspace, archiveCycle, navigate, displayByCycleId, lifecycleDisplayLoading]);

  // ── List row ────────────────────────────────────────────────────────────────
  const renderListRow = (cycle: CycleItem) => {
    const isPinned = pinnedCycleId === cycle.id;
    const isEditing = editingCycleId === cycle.id;
    const { label: modeLabel, Icon: ModeIcon } = modeInfo(cycle.cadence);
    return (
      <div
        key={cycle.id}
        onClick={() => { if (!isEditing) { setActiveCycle(cycle.id); navigate(`/prioritization/cycles/${cycle.id}`); } }}
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${isPinned ? 'bg-[var(--mustard-dim)]/40 hover:bg-[var(--mustard-dim)]' : 'hover:bg-[var(--surface)]/60'}`}
      >
        <button onClick={e => { e.stopPropagation(); if (isPinned) { exitWorkspace(); } else { enterWorkspace(cycle.id, cycle.name); navigate(`/prioritization/cycles/${cycle.id}`); } }}
          className={`flex-shrink-0 p-1 rounded transition-colors ${isPinned ? 'text-[var(--mustard)]' : 'text-[var(--text-ter)] hover:text-[var(--mustard)]'}`}>
          <Star className={`h-3.5 w-3.5 ${isPinned ? 'fill-amber-400' : ''}`} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isEditing ? (
              <input
                ref={editInputRef}
                value={editingName}
                onChange={e => setEditingName(e.target.value)}
                onBlur={commitCycleRename}
                onKeyDown={e => { if (e.key === 'Enter') commitCycleRename(); if (e.key === 'Escape') setEditingCycleId(null); }}
                className="text-sm font-medium text-[var(--text-pri)] border border-primary-400 rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary-400 bg-[var(--bg-raised)] min-w-0 max-w-[280px]"
                onClick={e => e.stopPropagation()}
              />
            ) : (
              <span
                className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-600 truncate"
                onDoubleClick={e => startEditingCycle(cycle, e)}
                title="Double-click to rename"
              >
                {cycle.name}
              </span>
            )}
            {lifecycleStatusCell(cycle.id, displayByCycleId, lifecycleDisplayLoading)}
            {isPinned && <span className="text-[10px] font-bold bg-[var(--mustard-dim)] text-[var(--mustard)] px-1.5 py-0.5 rounded uppercase tracking-wide">Active</span>}
          </div>
          {cycle.description && (
            <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">{cycle.description}</p>
          )}
          <div className="mt-0.5 flex items-center gap-3 text-xs text-[var(--text-ter)]">
            <span className="flex items-center gap-1"><ModeIcon className="h-3 w-3" />{modeLabel}</span>
            <span className="flex items-center gap-1"><Layers className="h-3 w-3" />{trackCountByCycle[cycle.id] ?? 0} track{(trackCountByCycle[cycle.id] ?? 0) !== 1 ? 's' : ''}</span>
            <span>Updated {formatDate(cycle.updatedAt)}</span>
          </div>
        </div>
        <div className="flex-shrink-0" onClick={e => e.stopPropagation()}>
          <CycleRowActions cycle={cycle} onArchive={() => archiveCycle(cycle.id)} onDelete={() => handleDelete(cycle.id, cycle.name)} onUnarchived={() => fetchFromSupabase()} canDelete={canDeleteCycle} canArchive={canArchiveCycle} launched={isCycleDesignerComplete(cycle as never)} canDesign={canDesignCycles} />
        </div>
      </div>
    );
  };

  if (syncState.isLoading && cycles.length === 0) {
    return (
      <div className="space-y-6">
        <CyclesPageHeader canCreate={canCreateCycle} onNewCycle={() => setShowCreateForm(true)} />
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-12 text-center dark:border-[var(--rule)] dark:bg-[var(--bg-raised)]">
          <Loader2 className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-3 animate-spin" />
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">Loading cycles…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <CyclesPageHeader canCreate={canCreateCycle} onNewCycle={() => setShowCreateForm(true)} />

      <Modal
        open={deleteBlocked !== null}
        onClose={() => setDeleteBlocked(null)}
        size="sm"
        title="This cycle was not deleted"
        description={deleteBlocked ? `"${deleteBlocked.name}" is still here. Nothing was changed.` : undefined}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setDeleteBlocked(null)}>
              Close
            </button>
            {deleteBlocked?.canArchive && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={archivingBlocked}
                onClick={() => void handleArchiveInstead()}
              >
                {archivingBlocked ? 'Archiving…' : 'Archive instead'}
              </button>
            )}
          </>
        }
      >
        <p className="text-sm text-[var(--text-sec)]">{deleteBlocked?.reason}</p>
        {deleteBlocked?.canArchive && (
          <p className="text-sm text-[var(--text-ter)] mt-3">
            Archiving hides the cycle from the active list and keeps its sessions and history. It can be undone.
          </p>
        )}
      </Modal>

      {/* The DEGAP framework banner was removed 2026-08-27. Explaining the
          framework is reference material — it now lives in the Cycles section of
          the in-app documentation, rather than sitting above the list you use
          every day. */}

      <EntityListToolbar
        searchQuery={listState.searchQuery}
        onSearchChange={listState.setSearchQuery}
        searchPlaceholder="Search cycles…"
        filterFields={FILTER_FIELDS}
        activeFilters={listState.activeFilters}
        onFilterChange={listState.setFieldFilter}
        onClearFilters={listState.clearAllFilters}
        filterCount={listState.filterCount}
        filterPanelOpen={listState.filterPanelOpen}
        onToggleFilterPanel={listState.toggleFilterPanel}
        sort={listState.sort}
        onSortChange={listState.setSort}
        sortOptions={SORT_OPTIONS}
        groupBy={listState.groupBy}
        onGroupByChange={listState.setGroupBy}
        groupOptions={GROUP_OPTIONS}
        allColumns={ALL_COLUMNS}
        visibleColumns={listState.visibleColumns}
        onToggleColumn={listState.toggleColumn}
        onReorderColumns={listState.reorderColumns}
        density={listState.density}
        onDensityChange={listState.setDensity}
        viewMode={listState.viewMode}
        onViewModeChange={listState.setViewMode}
        totalCount={cycles.length}
        filteredCount={filtered.length}
      />

      {/* Content */}
      {filtered.length === 0 ? (
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-12 text-center dark:border-[var(--rule)] dark:bg-[var(--bg-raised)]">
          <p className="text-sm font-medium text-[var(--text-sec)] mb-1">
            {listState.searchQuery ? 'No cycles match your search' : 'No cycles yet'}
          </p>
          <p className="text-xs text-[var(--text-ter)]">
            {listState.searchQuery ? 'Try a different search term.' : 'Create a cycle to start a structured prioritization effort.'}
          </p>
          {!listState.searchQuery && canCreateCycle && (
            <button onClick={() => setShowCreateForm(true)} className="mt-4 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors">
              <Plus className="h-4 w-4" />New Cycle
            </button>
          )}
        </div>
      ) : listState.viewMode === 'table' ? (
        <div className="divide-y divide-[var(--rule)] overflow-x-auto border border-[var(--rule)] bg-[var(--bg-raised)] dark:divide-[var(--rule)] dark:border-[var(--rule)] dark:bg-[var(--bg-raised)]">
          {groupedItems ? (
        Array.from(groupedItems.entries()).map(([groupKey, items]) => (
            <div key={groupKey}>
              <div
                className="flex cursor-pointer items-center gap-2 border-b border-[var(--rule)] bg-[var(--surface)] px-4 py-2 transition-colors hover:bg-[var(--hover-tint)] dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
                onClick={() => listState.toggleGroup(groupKey)}
              >
                <ChevronRight className={`h-3.5 w-3.5 text-[var(--text-ter)] transition-transform ${listState.isGroupOpen(groupKey) ? 'rotate-90' : ''}`} />
                <span className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide">{groupKey}</span>
                <span className="text-xs text-[var(--text-ter)] ml-1">{items.length}</span>
              </div>
              {listState.isGroupOpen(groupKey) && (
                <VirtualizedTable
                  items={items}
                  getId={c => c.id}
                  columns={tableColumns}
                  rowHeight={rowHeight}
                  selectedIds={new Set()}
                  onToggle={() => {}}
                  selectable={false}
                  onRowClick={cycle => { setActiveCycle(cycle.id); navigate(`/prioritization/cycles/${cycle.id}`); }}
                  currentSort={{ field: listState.sort.field as never, direction: listState.sort.direction }}
                  onSortChange={field => listState.toggleSort(field as string)}
                  height={Math.min(items.length * rowHeight + 40, 600)}
                />
              )}
            </div>
          ))
          ) : (
            <VirtualizedTable
              items={filtered}
              getId={c => c.id}
              columns={tableColumns}
              rowHeight={rowHeight}
              selectedIds={new Set()}
              onToggle={() => {}}
              selectable={false}
              onRowClick={cycle => { setActiveCycle(cycle.id); navigate(`/prioritization/cycles/${cycle.id}`); }}
              currentSort={{ field: listState.sort.field as never, direction: listState.sort.direction }}
              onSortChange={field => listState.toggleSort(field as string)}
              height={Math.min(filtered.length * rowHeight + 40, 700)}
            />
          )}
        </div>
      ) : (
        /* List view */
        <div className="divide-y divide-[var(--rule)] overflow-x-auto border border-[var(--rule)] bg-[var(--bg-raised)] dark:divide-[var(--rule)] dark:border-[var(--rule)] dark:bg-[var(--bg-raised)]">
          {groupedItems ? (
            Array.from(groupedItems.entries()).map(([groupKey, items]) => (
              <div key={groupKey}>
                <div
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--surface)] cursor-pointer hover:bg-[var(--hover-tint)] transition-colors"
                  onClick={() => listState.toggleGroup(groupKey)}
                >
                  <ChevronRight className={`h-3.5 w-3.5 text-[var(--text-ter)] transition-transform ${listState.isGroupOpen(groupKey) ? 'rotate-90' : ''}`} />
                  <span className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide">{groupKey}</span>
                  <span className="text-xs text-[var(--text-ter)] ml-1">{items.length}</span>
                </div>
                {listState.isGroupOpen(groupKey) && (
                  <div className="divide-y divide-[var(--rule)]">
                    {items.map(renderListRow)}
                  </div>
                )}
              </div>
            ))
          ) : (
            filtered.map(renderListRow)
          )}
        </div>
      )}

      {/* Catchball Prompt — shown after cycle creation when config requires Catchball (Thing 1B) */}
      {catchballPromptCycleId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] w-full max-w-md p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-1">Cycle created</h2>
            <div className="border border-primary-200 bg-primary-50 p-4 mt-3">
              <p className="text-sm font-medium text-primary-800">
                This configuration requires Catchball
              </p>
              <p className="text-sm text-primary-700 mt-1">
                Configure Catchball now to set up governance tiers, ball holders,
                and evaluation rules — or do it from the cycle settings later.
              </p>
              <div className="flex gap-3 mt-3">
                <Link
                  to={`/prioritization/cascade/configure/${catchballPromptCycleId}`}
                  className="text-sm font-medium text-primary-700 underline"
                  onClick={() => setCatchballPromptCycleId(null)}
                >
                  Configure now
                </Link>
                <button
                  type="button"
                  onClick={() => setCatchballPromptCycleId(null)}
                  className="text-sm text-primary-600"
                >
                  Do it later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CreateCycleDialog
        open={showCreateForm}
        onClose={closeCreateForm}
        configuration={pendingConfiguration}
        initialName={pendingConfiguration?.name ?? undefined}
      />
    </div>
  );
}

// ── DEGAP process model ───────────────────────────────────────────────────────

// Light + dark: saturated pills read as hue on dark surfaces without a full-bleed light banner.
function CyclesPageHeader({ canCreate, onNewCycle }: { canCreate: boolean; onNewCycle: () => void }) {
  return (
    <SharedPageHeader
      icon={<Calendar className="h-4 w-4 text-[var(--text-ter)] dark:text-primary-400" />}
      iconBg="bg-[var(--slate-dim)] dark:bg-primary-950/40"
      title="Cycles"
      subtitle="Time-bound containers for priority formation. Each Track operates independently within a cycle."
      actions={canCreate ? (
        <button onClick={onNewCycle} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors">
          <Plus className="h-4 w-4" />New Cycle
        </button>
      ) : undefined}
    />
  );
}
